import type { LocaleId } from '../locales';
import type { MotionSplit } from './schema';
import { SHOWN_INSET, type ClipInset, type Pose } from './pose';

/**
 * Preset catalog. Enter presets describe the way in, from the hidden pose to the rest pose
 * (identity); the same preset used as an exit is played backwards. Emphasis presets start and end
 * at identity and are composited on top of whatever else the layer does.
 */

/** Labels in LOCALE_IDS order: en, uk, pl, ro, es, ru. */
export type Label = readonly [string, string, string, string, string, string];

export function label(l: Label, locale: LocaleId): string {
  const i = { en: 0, uk: 1, pl: 2, ro: 3, es: 4, ru: 5 }[locale] ?? 0;
  return l[i] || l[0];
}

export type Direction = 'up' | 'down' | 'left' | 'right';
export const DIRECTIONS: Direction[] = ['up', 'down', 'left', 'right'];

export interface PresetParam {
  key: string;
  /**
   * `color`: a palette key (accent, accent2, dark, light) or a colour; `layer`: a layer id
   * ('' = picked automatically); `text`: free text ('' = the headline of the template); `font`: a
   * font family ('' = the font of the source layer or the headline).
   */
  type: 'direction' | 'number' | 'select' | 'color' | 'text' | 'longtext' | 'layer' | 'font';
  default: number | string;
  options?: Array<{ value: string; label: Label }>;
  min?: number;
  max?: number;
  step?: number;
  label: Label;
}

export type PresetParams = Record<string, number | string | boolean>;

export interface PresetContext {
  params: PresetParams;
}

export type PresetCategory = 'basic' | 'dynamic' | 'reveal' | 'text' | 'cinematic';

export interface MotionPreset {
  id: string;
  label: Label;
  category: PresetCategory;
  /** Typical length and curve; steps may override both. */
  duration: number;
  easing: string;
  /** Curve used when the preset plays as an exit. */
  exitEasing?: string;
  params?: PresetParam[];
  /** Text animator the preset is designed for (applied when picked from the editor). */
  split?: Partial<MotionSplit>;
  poses: (ctx: PresetContext) => Pose[];
}

export interface EmphasisPreset {
  id: string;
  label: Label;
  duration: number;
  easing: string;
  /** Suggested repeat count (0 = until the end). */
  repeat: number;
  /** Long, slow presets meant to span the whole scene (Ken Burns, float). */
  ambient?: boolean;
  params?: PresetParam[];
  poses: (ctx: PresetContext) => Pose[];
}

const num = (p: PresetParams, k: string, d: number) => {
  const v = p[k];
  return typeof v === 'number' && Number.isFinite(v) ? v : d;
};
const dir = (p: PresetParams, d: Direction = 'up'): Direction => {
  const v = p.direction;
  return typeof v === 'string' && (DIRECTIONS as string[]).includes(v) ? (v as Direction) : d;
};

/** Offset (in % of the box) the element comes from when it enters moving in `d`. */
function from(d: Direction, distance: number): { x: number; y: number } {
  switch (d) {
    case 'up':
      return { x: 0, y: distance };
    case 'down':
      return { x: 0, y: -distance };
    case 'left':
      return { x: distance, y: 0 };
    case 'right':
      return { x: -distance, y: 0 };
  }
}

/** Clip that hides the box on the side the reveal starts from. */
function wipeFrom(d: Direction): ClipInset {
  const c = { ...SHOWN_INSET };
  // moving `up` reveals bottom → top: everything above the edge is hidden
  if (d === 'up') c.top = 100;
  if (d === 'down') c.bottom = 100;
  if (d === 'left') c.left = 100;
  if (d === 'right') c.right = 100;
  return c;
}

const P_DIRECTION = (d: Direction = 'up'): PresetParam => ({
  key: 'direction',
  type: 'direction',
  default: d,
  label: ['Direction', 'Напрямок', 'Kierunek', 'Direcție', 'Dirección', 'Направление'],
});
const P_DISTANCE = (d: number, max = 300): PresetParam => ({
  key: 'distance',
  type: 'number',
  default: d,
  min: 0,
  max,
  step: 5,
  label: [
    'Distance, %',
    'Відстань, %',
    'Odległość, %',
    'Distanță, %',
    'Distancia, %',
    'Расстояние, %',
  ],
});
const P_INTENSITY: PresetParam = {
  key: 'intensity',
  type: 'number',
  default: 1,
  min: 0.2,
  max: 3,
  step: 0.1,
  label: ['Intensity', 'Сила', 'Siła', 'Intensitate', 'Intensidad', 'Сила'],
};

export const ENTER_PRESETS: MotionPreset[] = [
  {
    id: 'fade',
    label: ['Fade', 'Поява', 'Pojawienie', 'Estompare', 'Fundido', 'Появление'],
    category: 'basic',
    duration: 500,
    easing: 'ease-out',
    exitEasing: 'ease-in',
    poses: () => [{ opacity: 0 }, { opacity: 1 }],
  },
  {
    id: 'rise',
    label: ['Rise', 'Підйом', 'Wznoszenie', 'Ridicare', 'Ascenso', 'Подъём'],
    category: 'basic',
    duration: 700,
    easing: 'out-expo',
    exitEasing: 'in-expo',
    params: [P_DIRECTION('up'), P_DISTANCE(35)],
    poses: ({ params }) => [
      { ...from(dir(params), num(params, 'distance', 35)), opacity: 0 },
      { x: 0, y: 0, opacity: 1 },
    ],
  },
  {
    id: 'slide',
    label: ['Slide', 'Виїзд', 'Wjazd', 'Glisare', 'Deslizar', 'Выезд'],
    category: 'basic',
    duration: 650,
    easing: 'out-quart',
    exitEasing: 'in-quart',
    params: [P_DIRECTION('left'), P_DISTANCE(120)],
    poses: ({ params }) => [
      { ...from(dir(params, 'left'), num(params, 'distance', 120)) },
      { x: 0, y: 0 },
    ],
  },
  {
    id: 'zoom-in',
    label: ['Zoom in', 'Наближення', 'Przybliżenie', 'Zoom înăuntru', 'Acercar', 'Приближение'],
    category: 'basic',
    duration: 600,
    easing: 'out-expo',
    exitEasing: 'in-expo',
    params: [P_INTENSITY],
    poses: ({ params }) => [
      { scale: Math.max(0, 1 - 0.4 * num(params, 'intensity', 1)), opacity: 0 },
      { scale: 1, opacity: 1 },
    ],
  },
  {
    id: 'zoom-out',
    label: ['Zoom out', 'Віддалення', 'Oddalenie', 'Zoom în afară', 'Alejar', 'Отдаление'],
    category: 'cinematic',
    duration: 800,
    easing: 'out-expo',
    exitEasing: 'in-expo',
    params: [P_INTENSITY],
    poses: ({ params }) => [
      { scale: 1 + 0.4 * num(params, 'intensity', 1), opacity: 0, blur: 8 },
      { scale: 1, opacity: 1, blur: 0 },
    ],
  },
  {
    id: 'pop',
    label: ['Pop', 'Поп', 'Pop', 'Pop', 'Pop', 'Поп'],
    category: 'dynamic',
    duration: 700,
    easing: 'spring-bouncy',
    exitEasing: 'in-back',
    poses: () => [
      { scale: 0, opacity: 0, offset: 0 },
      { scale: 0.35, opacity: 1, offset: 0.12 },
      { scale: 1, opacity: 1, offset: 1 },
    ],
  },
  {
    id: 'drop',
    label: ['Drop', 'Падіння', 'Upadek', 'Cădere', 'Caída', 'Падение'],
    category: 'dynamic',
    duration: 900,
    easing: 'bounce',
    exitEasing: 'in-back',
    params: [P_DISTANCE(80)],
    poses: ({ params }) => [
      { y: -num(params, 'distance', 80), opacity: 0, offset: 0 },
      { y: -num(params, 'distance', 80) * 0.85, opacity: 1, offset: 0.08 },
      { y: 0, opacity: 1, offset: 1 },
    ],
  },
  {
    id: 'blur-in',
    label: ['Blur in', 'Проявлення', 'Wyostrzenie', 'Clarificare', 'Desenfoque', 'Проявление'],
    category: 'cinematic',
    duration: 900,
    easing: 'out-quart',
    exitEasing: 'in-quart',
    poses: () => [
      { opacity: 0, blur: 24, scale: 1.06 },
      { opacity: 1, blur: 0, scale: 1 },
    ],
  },
  {
    id: 'focus',
    label: ['Focus pull', 'Фокус', 'Ostrość', 'Focalizare', 'Enfoque', 'Фокус'],
    category: 'cinematic',
    duration: 1200,
    easing: 'out-expo',
    exitEasing: 'in-expo',
    poses: () => [
      { opacity: 0, blur: 40, scale: 1.35 },
      { opacity: 1, blur: 0, scale: 1 },
    ],
  },
  {
    id: 'drift',
    label: ['Drift', 'Дрейф', 'Dryf', 'Derivă', 'Deriva', 'Дрейф'],
    category: 'cinematic',
    duration: 1400,
    easing: 'out-quart',
    exitEasing: 'in-quart',
    params: [P_DIRECTION('right'), P_DISTANCE(10, 60)],
    poses: ({ params }) => [
      { ...from(dir(params, 'right'), num(params, 'distance', 10)), opacity: 0 },
      { x: 0, y: 0, opacity: 1 },
    ],
  },
  {
    id: 'flip',
    label: ['Flip', 'Переворот', 'Obrót', 'Răsturnare', 'Volteo', 'Переворот'],
    category: 'dynamic',
    duration: 800,
    easing: 'out-back',
    exitEasing: 'in-back',
    params: [P_DIRECTION('up')],
    poses: ({ params }) => {
      const d = dir(params);
      const axis = d === 'up' || d === 'down' ? 'rotateX' : 'rotateY';
      const sign = d === 'up' || d === 'right' ? 1 : -1;
      return [
        { [axis]: 90 * sign, opacity: 0, offset: 0 },
        { [axis]: 60 * sign, opacity: 1, offset: 0.2 },
        { [axis]: 0, opacity: 1, offset: 1 },
      ];
    },
  },
  {
    id: 'tilt',
    label: ['Tilt', 'Нахил', 'Przechył', 'Înclinare', 'Inclinación', 'Наклон'],
    category: 'cinematic',
    duration: 900,
    easing: 'out-expo',
    exitEasing: 'in-expo',
    poses: () => [
      { rotateY: -55, x: -15, opacity: 0 },
      { rotateY: 0, x: 0, opacity: 1 },
    ],
  },
  {
    id: 'swing',
    label: ['Swing in', 'Розгойдування', 'Wahnięcie', 'Balans', 'Balanceo', 'Раскачивание'],
    category: 'dynamic',
    duration: 900,
    easing: 'spring',
    exitEasing: 'in-back',
    poses: () => [
      { rotate: -18, y: 10, opacity: 0, offset: 0 },
      { rotate: -12, y: 6, opacity: 1, offset: 0.15 },
      { rotate: 0, y: 0, opacity: 1, offset: 1 },
    ],
  },
  {
    id: 'spin',
    label: ['Spin in', 'Обертання', 'Obrót wejście', 'Rotire', 'Giro', 'Вращение'],
    category: 'dynamic',
    duration: 900,
    easing: 'out-back',
    exitEasing: 'in-back',
    poses: () => [
      { rotate: -200, scale: 0.2, opacity: 0 },
      { rotate: 0, scale: 1, opacity: 1 },
    ],
  },
  {
    id: 'roll',
    label: ['Roll in', 'Накат', 'Wtoczenie', 'Rostogolire', 'Rodar', 'Накат'],
    category: 'dynamic',
    duration: 900,
    easing: 'out-quart',
    exitEasing: 'in-quart',
    params: [P_DIRECTION('right')],
    poses: ({ params }) => {
      const d = dir(params, 'right');
      const sign = d === 'left' ? -1 : 1;
      return [
        { x: -120 * sign, rotate: -120 * sign, opacity: 0 },
        { x: 0, rotate: 0, opacity: 1 },
      ];
    },
  },
  {
    id: 'whip',
    label: ['Whip', 'Ривок', 'Szarpnięcie', 'Bici', 'Látigo', 'Рывок'],
    category: 'dynamic',
    duration: 550,
    easing: 'snappy',
    exitEasing: 'in-expo',
    params: [P_DIRECTION('right')],
    poses: ({ params }) => {
      const d = dir(params, 'right');
      const f = from(d, 140);
      const skew = d === 'left' ? 18 : d === 'right' ? -18 : 0;
      return [
        { ...f, skewX: skew, blur: 14, opacity: 0, offset: 0 },
        { x: f.x * 0.15, y: f.y * 0.15, skewX: skew * 0.4, blur: 4, opacity: 1, offset: 0.55 },
        { x: 0, y: 0, skewX: 0, blur: 0, opacity: 1, offset: 1 },
      ];
    },
  },
  {
    id: 'stretch',
    label: ['Stretch', 'Пружина', 'Sprężyna', 'Elastic', 'Estirar', 'Пружина'],
    category: 'dynamic',
    duration: 800,
    easing: 'spring-bouncy',
    exitEasing: 'in-back',
    poses: () => [
      { scaleX: 0.2, scaleY: 1.6, opacity: 0, offset: 0 },
      { scaleX: 0.4, scaleY: 1.4, opacity: 1, offset: 0.1 },
      { scaleX: 1, scaleY: 1, opacity: 1, offset: 1 },
    ],
  },
  {
    id: 'elastic',
    label: ['Elastic', 'Еластик', 'Elastyczny', 'Elastic', 'Elástico', 'Эластик'],
    category: 'dynamic',
    duration: 1100,
    easing: 'elastic',
    exitEasing: 'in-back',
    poses: () => [
      { scale: 0.3, opacity: 0, offset: 0 },
      { scale: 0.45, opacity: 1, offset: 0.06 },
      { scale: 1, opacity: 1, offset: 1 },
    ],
  },
  {
    id: 'unfold',
    label: ['Unfold', 'Розгортання', 'Rozwinięcie', 'Desfășurare', 'Desplegar', 'Разворот'],
    category: 'reveal',
    duration: 700,
    easing: 'out-expo',
    exitEasing: 'in-expo',
    params: [P_DIRECTION('up')],
    poses: ({ params }) => {
      const d = dir(params);
      return d === 'up' || d === 'down'
        ? [
            { scaleY: 0, opacity: 0, offset: 0 },
            { scaleY: 0.1, opacity: 1, offset: 0.05 },
            { scaleY: 1, opacity: 1 },
          ]
        : [
            { scaleX: 0, opacity: 0, offset: 0 },
            { scaleX: 0.1, opacity: 1, offset: 0.05 },
            { scaleX: 1, opacity: 1 },
          ];
    },
  },
  {
    id: 'wipe',
    label: ['Wipe', 'Шторка', 'Kurtyna', 'Cortină', 'Cortinilla', 'Шторка'],
    category: 'reveal',
    duration: 800,
    easing: 'in-out-quart',
    exitEasing: 'in-out-quart',
    params: [P_DIRECTION('right')],
    poses: ({ params }) => [{ clip: wipeFrom(dir(params, 'right')) }, { clip: SHOWN_INSET }],
  },
  {
    id: 'iris',
    label: ['Iris', 'Діафрагма', 'Przesłona', 'Diafragmă', 'Iris', 'Диафрагма'],
    category: 'reveal',
    duration: 900,
    easing: 'in-out-quart',
    exitEasing: 'in-out-quart',
    poses: () => [{ clip: { type: 'circle', r: 0 } }, { clip: { type: 'circle', r: 80 } }],
  },
  {
    id: 'split',
    label: ['Split', 'Розкриття', 'Rozsunięcie', 'Despicare', 'Apertura', 'Раскрытие'],
    category: 'reveal',
    duration: 800,
    easing: 'in-out-expo',
    exitEasing: 'in-out-expo',
    params: [P_DIRECTION('right')],
    poses: ({ params }) => {
      const d = dir(params, 'right');
      const hidden =
        d === 'up' || d === 'down'
          ? { ...SHOWN_INSET, top: 50, bottom: 50 }
          : { ...SHOWN_INSET, left: 50, right: 50 };
      return [{ clip: hidden }, { clip: SHOWN_INSET }];
    },
  },
  {
    id: 'mask-up',
    label: ['Mask reveal', 'Маска', 'Maska', 'Mască', 'Máscara', 'Маска'],
    category: 'reveal',
    duration: 800,
    easing: 'out-expo',
    exitEasing: 'in-expo',
    params: [P_DIRECTION('up')],
    // The content slides in while the clip moves the other way, so it seems to rise out of an
    // invisible slot at its own position.
    poses: ({ params }) => {
      const d = dir(params);
      const f = from(d, 100);
      const c = { ...SHOWN_INSET };
      if (d === 'up') c.bottom = 100;
      if (d === 'down') c.top = 100;
      if (d === 'left') c.right = 100;
      if (d === 'right') c.left = 100;
      return [
        { ...f, clip: c },
        { x: 0, y: 0, clip: { type: 'inset', top: 0, right: 0, bottom: 0, left: 0 } },
      ];
    },
  },
  {
    id: 'glitch',
    label: ['Glitch', 'Глітч', 'Glitch', 'Glitch', 'Glitch', 'Глитч'],
    category: 'dynamic',
    duration: 600,
    easing: 'linear',
    exitEasing: 'linear',
    poses: () => [
      { x: -6, skewX: 12, opacity: 0, brightness: 2, offset: 0 },
      { x: 5, skewX: -8, opacity: 1, brightness: 1.6, offset: 0.15 },
      { x: -3, skewX: 6, opacity: 0.2, brightness: 1.8, offset: 0.3 },
      { x: 2, skewX: -3, opacity: 1, brightness: 1.2, offset: 0.5 },
      { x: -1, skewX: 1, opacity: 0.6, brightness: 1.4, offset: 0.65 },
      { x: 0, skewX: 0, opacity: 1, brightness: 1, offset: 1 },
    ],
  },
  // Text animators: the same moves applied to lines, words or letters one after another
  {
    id: 'words-rise',
    label: [
      'Words rise',
      'Слова вгору',
      'Słowa w górę',
      'Cuvinte urcă',
      'Palabras suben',
      'Слова вверх',
    ],
    category: 'text',
    duration: 650,
    easing: 'out-expo',
    exitEasing: 'in-expo',
    split: { by: 'word', stagger: 70, order: 'start' },
    poses: () => [
      { y: 60, opacity: 0 },
      { y: 0, opacity: 1 },
    ],
  },
  {
    id: 'lines-mask',
    label: [
      'Lines reveal',
      'Рядки з маски',
      'Linie z maski',
      'Rânduri din mască',
      'Líneas máscara',
      'Строки из маски',
    ],
    category: 'text',
    duration: 800,
    easing: 'out-expo',
    exitEasing: 'in-expo',
    split: { by: 'line', stagger: 120, order: 'start' },
    poses: () => [
      { y: 100, clip: { type: 'inset', top: 0, right: -5, bottom: 100, left: -5 } },
      { y: 0, clip: { type: 'inset', top: 0, right: -5, bottom: 0, left: -5 } },
    ],
  },
  {
    id: 'letters-pop',
    label: ['Letters pop', 'Літери поп', 'Litery pop', 'Litere pop', 'Letras pop', 'Буквы поп'],
    category: 'text',
    duration: 550,
    easing: 'spring-bouncy',
    exitEasing: 'in-back',
    split: { by: 'char', stagger: 35, order: 'start' },
    poses: () => [
      { scale: 0, opacity: 0, offset: 0 },
      { scale: 0.3, opacity: 1, offset: 0.1 },
      { scale: 1, opacity: 1, offset: 1 },
    ],
  },
  {
    id: 'letters-drop',
    label: [
      'Letters drop',
      'Літери падають',
      'Litery spadają',
      'Litere cad',
      'Letras caen',
      'Буквы падают',
    ],
    category: 'text',
    duration: 700,
    easing: 'bounce',
    exitEasing: 'in-back',
    split: { by: 'char', stagger: 40, order: 'random' },
    poses: () => [
      { y: -150, opacity: 0, offset: 0 },
      { y: -120, opacity: 1, offset: 0.08 },
      { y: 0, opacity: 1, offset: 1 },
    ],
  },
  {
    id: 'letters-blur',
    label: [
      'Letters blur',
      'Літери з розмиття',
      'Litery z rozmycia',
      'Litere din ceață',
      'Letras difusas',
      'Буквы из размытия',
    ],
    category: 'text',
    duration: 800,
    easing: 'out-quart',
    exitEasing: 'in-quart',
    split: { by: 'char', stagger: 30, order: 'center' },
    poses: () => [
      { opacity: 0, blur: 16, scale: 1.6 },
      { opacity: 1, blur: 0, scale: 1 },
    ],
  },
  {
    id: 'typewriter',
    label: [
      'Typewriter',
      'Друкарська машинка',
      'Maszyna do pisania',
      'Mașină de scris',
      'Máquina de escribir',
      'Печатная машинка',
    ],
    category: 'text',
    duration: 40,
    easing: 'steps(1, jump-start)',
    exitEasing: 'steps(1, jump-end)',
    split: { by: 'char', stagger: 55, order: 'start' },
    poses: () => [{ opacity: 0 }, { opacity: 1 }],
  },
  {
    id: 'words-flip',
    label: [
      'Words flip',
      'Слова переворот',
      'Słowa obrót',
      'Cuvinte răsturnate',
      'Palabras giran',
      'Слова переворот',
    ],
    category: 'text',
    duration: 700,
    easing: 'out-back',
    exitEasing: 'in-back',
    split: { by: 'word', stagger: 90, order: 'start' },
    poses: () => [
      { rotateX: -90, y: 20, opacity: 0, offset: 0 },
      { rotateX: -60, y: 14, opacity: 1, offset: 0.2 },
      { rotateX: 0, y: 0, opacity: 1, offset: 1 },
    ],
  },
];

export const EMPHASIS_PRESETS: EmphasisPreset[] = [
  {
    id: 'pulse',
    label: ['Pulse', 'Пульс', 'Puls', 'Puls', 'Pulso', 'Пульс'],
    duration: 900,
    easing: 'ease-in-out',
    repeat: 2,
    params: [P_INTENSITY],
    poses: ({ params }) => [
      { scale: 1 },
      { scale: 1 + 0.08 * num(params, 'intensity', 1) },
      { scale: 1 },
    ],
  },
  {
    id: 'heartbeat',
    label: ['Heartbeat', 'Серцебиття', 'Bicie serca', 'Bătăi inimă', 'Latido', 'Сердцебиение'],
    duration: 1100,
    easing: 'ease-in-out',
    repeat: 2,
    poses: () => [
      { scale: 1, offset: 0 },
      { scale: 1.14, offset: 0.14 },
      { scale: 1, offset: 0.28 },
      { scale: 1.14, offset: 0.42 },
      { scale: 1, offset: 0.7 },
      { scale: 1, offset: 1 },
    ],
  },
  {
    id: 'shake',
    label: ['Shake', 'Тряска', 'Wstrząs', 'Scuturare', 'Sacudida', 'Тряска'],
    duration: 600,
    easing: 'ease-in-out',
    repeat: 1,
    params: [P_INTENSITY],
    poses: ({ params }) => {
      const k = num(params, 'intensity', 1);
      return [0, -6, 6, -5, 5, -3, 3, 0].map((x) => ({ x: x * k }));
    },
  },
  {
    id: 'wobble',
    label: ['Wobble', 'Хитання', 'Chybotanie', 'Clătinare', 'Tambaleo', 'Шатание'],
    duration: 900,
    easing: 'ease-in-out',
    repeat: 1,
    poses: () => [
      { x: 0, rotate: 0 },
      { x: -12, rotate: -5 },
      { x: 9, rotate: 3 },
      { x: -6, rotate: -3 },
      { x: 4, rotate: 2 },
      { x: 0, rotate: 0 },
    ],
  },
  {
    id: 'swing',
    label: ['Swing', 'Гойдалка', 'Huśtawka', 'Leagăn', 'Columpio', 'Качели'],
    duration: 1000,
    easing: 'ease-in-out',
    repeat: 1,
    poses: () => [
      { rotate: 0 },
      { rotate: 12 },
      { rotate: -9 },
      { rotate: 5 },
      { rotate: -3 },
      { rotate: 0 },
    ],
  },
  {
    id: 'bounce',
    label: ['Bounce', 'Стрибок', 'Podskok', 'Săritură', 'Rebote', 'Прыжок'],
    duration: 1000,
    easing: 'ease-in-out',
    repeat: 1,
    poses: () => [
      { y: 0, scaleY: 1, offset: 0 },
      { y: 0, scaleY: 0.92, scaleX: 1.06, offset: 0.12 },
      { y: -22, scaleY: 1.04, scaleX: 0.97, offset: 0.4 },
      { y: 0, scaleY: 0.95, scaleX: 1.04, offset: 0.62 },
      { y: -7, scaleY: 1, scaleX: 1, offset: 0.8 },
      { y: 0, scaleY: 1, scaleX: 1, offset: 1 },
    ],
  },
  {
    id: 'tada',
    label: ['Tada', 'Тада', 'Tada', 'Tada', 'Tachán', 'Тада'],
    duration: 1000,
    easing: 'ease-in-out',
    repeat: 1,
    poses: () => [
      { scale: 1, rotate: 0 },
      { scale: 0.9, rotate: -3 },
      { scale: 1.1, rotate: 3 },
      { scale: 1.1, rotate: -3 },
      { scale: 1.1, rotate: 3 },
      { scale: 1.1, rotate: -3 },
      { scale: 1, rotate: 0 },
    ],
  },
  {
    id: 'jello',
    label: ['Jello', 'Желе', 'Galaretka', 'Jeleu', 'Gelatina', 'Желе'],
    duration: 900,
    easing: 'ease-in-out',
    repeat: 1,
    poses: () => [0, -12.5, 6.25, -3.1, 1.5, -0.8, 0].map((s) => ({ skewX: s, skewY: s })),
  },
  {
    id: 'rubber',
    label: ['Rubber band', 'Гумка', 'Gumka', 'Elastic', 'Goma', 'Резинка'],
    duration: 900,
    easing: 'ease-in-out',
    repeat: 1,
    poses: () => [
      { scaleX: 1, scaleY: 1 },
      { scaleX: 1.25, scaleY: 0.75 },
      { scaleX: 0.8, scaleY: 1.2 },
      { scaleX: 1.12, scaleY: 0.88 },
      { scaleX: 0.96, scaleY: 1.04 },
      { scaleX: 1, scaleY: 1 },
    ],
  },
  {
    id: 'flash',
    label: ['Flash', 'Блимання', 'Mruganie', 'Clipire', 'Destello', 'Мигание'],
    duration: 800,
    easing: 'linear',
    repeat: 1,
    poses: () => [
      { opacity: 1 },
      { opacity: 0.15 },
      { opacity: 1 },
      { opacity: 0.15 },
      { opacity: 1 },
    ],
  },
  {
    id: 'shine',
    label: ['Shine', 'Сяйво', 'Błysk', 'Strălucire', 'Brillo', 'Сияние'],
    duration: 900,
    easing: 'ease-in-out',
    repeat: 2,
    poses: () => [{ brightness: 1 }, { brightness: 1.45 }, { brightness: 1 }],
  },
  {
    id: 'wiggle',
    label: ['Wiggle', 'Покачування', 'Kiwanie', 'Mișcare', 'Meneo', 'Покачивание'],
    duration: 500,
    easing: 'ease-in-out',
    repeat: 3,
    poses: () => [{ rotate: 0 }, { rotate: -4 }, { rotate: 4 }, { rotate: -2 }, { rotate: 0 }],
  },
  {
    id: 'spin',
    label: ['Spin', 'Оберт', 'Obrót', 'Rotire', 'Giro', 'Оборот'],
    duration: 1200,
    easing: 'in-out-quart',
    repeat: 1,
    poses: () => [{ rotate: 0 }, { rotate: 360 }],
  },
  {
    id: 'float',
    label: ['Float', 'Ширяння', 'Unoszenie', 'Plutire', 'Flotar', 'Парение'],
    duration: 3000,
    easing: 'ease-in-out',
    repeat: 0,
    ambient: true,
    params: [P_INTENSITY],
    poses: ({ params }) => {
      const k = num(params, 'intensity', 1);
      return [
        { y: 0, rotate: 0 },
        { y: -4 * k, rotate: 1 * k },
        { y: 0, rotate: 0 },
      ];
    },
  },
  {
    id: 'breathe',
    label: ['Breathe', 'Дихання', 'Oddech', 'Respirație', 'Respirar', 'Дыхание'],
    duration: 3200,
    easing: 'ease-in-out',
    repeat: 0,
    ambient: true,
    poses: () => [{ scale: 1 }, { scale: 1.035 }, { scale: 1 }],
  },
  {
    id: 'ken-burns',
    label: ['Ken Burns', 'Кен Бернс', 'Ken Burns', 'Ken Burns', 'Ken Burns', 'Кен Бёрнс'],
    duration: 6000,
    easing: 'linear',
    repeat: 1,
    ambient: true,
    params: [P_DIRECTION('right'), P_INTENSITY],
    poses: ({ params }) => {
      const k = num(params, 'intensity', 1);
      const d = dir(params, 'right');
      const pan = from(d, -3 * k);
      return [
        { scale: 1, x: 0, y: 0 },
        { scale: 1 + 0.12 * k, x: pan.x, y: pan.y },
      ];
    },
  },
];

export const ENTER_PRESET_MAP = new Map(ENTER_PRESETS.map((p) => [p.id, p]));
export const EMPHASIS_PRESET_MAP = new Map(EMPHASIS_PRESETS.map((p) => [p.id, p]));

export const PRESET_CATEGORIES: Array<{ id: PresetCategory; label: Label }> = [
  { id: 'basic', label: ['Basic', 'Базові', 'Podstawowe', 'De bază', 'Básicos', 'Базовые'] },
  {
    id: 'dynamic',
    label: ['Dynamic', 'Динамічні', 'Dynamiczne', 'Dinamice', 'Dinámicos', 'Динамичные'],
  },
  {
    id: 'cinematic',
    label: ['Cinematic', 'Кіношні', 'Filmowe', 'Cinematice', 'Cinemáticos', 'Киношные'],
  },
  {
    id: 'reveal',
    label: ['Reveal', 'Розкриття', 'Odsłonięcie', 'Dezvăluire', 'Revelado', 'Раскрытие'],
  },
  { id: 'text', label: ['Text', 'Текст', 'Tekst', 'Text', 'Texto', 'Текст'] },
];
